"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import "./AiChatBotDashboardSection.css";

const USE_CASES = [
  {
    id: "nlu",
    label: "Smart NLU",
    userMessage: "Analyze sentiment and translate: 'This is brilliant!'",
    botMessage: "Intent: Feedback | Sentiment: Positive (98%) | Spanish: '¡Esto es brillante!'",
    steps: ["IDENTIFY SENTIMENT & LANG", "PARSE ENTITIES & TRANS"],
    status: [
      "STANDBY",
      "PARSING SENTIMENT...",
      "TRANSLATING TEXT...",
      "COMPILING METRICS...",
      "COMPLETED"
    ]
  },
  {
    id: "qualify",
    label: "Lead Qualify",
    userMessage: "Can we schedule a demo for 150 team members?",
    botMessage: "Enterprise query identified. Routing details to your local account rep...",
    steps: ["EVALUATE LEAD METRICS", "SCHEDULE CALENDAR EVENT"],
    status: [
      "STANDBY",
      "CLASSIFYING LEAD...",
      "ROUTING TO CRM...",
      "SCHEDULING CALL...",
      "COMPLETED"
    ]
  },
  {
    id: "rag",
    label: "RAG Hub",
    userMessage: "Can you help cancel my plan?",
    botMessage: "Plan cancelled! $49 refund processed successfully.",
    steps: ["CLASSIFY CUSTOMER INTENT", "RETRIEVE KNOWLEDGE VIA RAG"],
    status: [
      "STANDBY",
      "ANALYZING INTENT...",
      "QUERYING VECTOR DB...",
      "GENERATING RESPONSE...",
      "COMPLETED"
    ]
  }
];

export default function AiChatBotDashboardSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const cardRefs = [useRef(null), useRef(null), useRef(null)];

  const [chatStep, setChatStep] = useState(0);
  const [chatTypedUser, setChatTypedUser] = useState("");
  const [chatTypedBot, setChatTypedBot] = useState("");
  const [selectedCaseIndex, setSelectedCaseIndex] = useState(2);

  const handleTagClick = (index) => {
    setSelectedCaseIndex(index);
    setChatStep(0);
  };

  useEffect(() => {
    const currentCase = USE_CASES[selectedCaseIndex];
    const userMessage = currentCase.userMessage;
    const botMessage = currentCase.botMessage;
    let timer;

    if (chatStep === 0) {
      setChatTypedUser("");
      setChatTypedBot("");
      let charIndex = 0;
      const typeUser = () => {
        if (charIndex < userMessage.length) {
          setChatTypedUser(userMessage.substring(0, charIndex + 1));
          charIndex++;
          timer = setTimeout(typeUser, 50);
        } else {
          timer = setTimeout(() => setChatStep(1), 1000);
        }
      };
      timer = setTimeout(typeUser, 600);
    } else if (chatStep === 1) {
      timer = setTimeout(() => setChatStep(2), 1400);
    } else if (chatStep === 2) {
      timer = setTimeout(() => setChatStep(3), 1400);
    } else if (chatStep === 3) {
      let charIndex = 0;
      const typeBot = () => {
        if (charIndex < botMessage.length) {
          setChatTypedBot(botMessage.substring(0, charIndex + 1));
          charIndex++;
          timer = setTimeout(typeBot, 45);
        } else {
          timer = setTimeout(() => setChatStep(4), 4000);
        }
      };
      timer = setTimeout(typeBot, 300);
    } else if (chatStep === 4) {
      timer = setTimeout(() => {
        setSelectedCaseIndex((prev) => (prev + 1) % USE_CASES.length);
        setChatStep(0);
      }, 1000);
    }

    return () => clearTimeout(timer);
  }, [chatStep, selectedCaseIndex]);

  const [workflowState, setWorkflowState] = useState(0);
  const [workflowLog, setWorkflowLog] = useState("Awaiting chatbot trigger...");

  useEffect(() => {
    const logs = [
      "Orchestrating flow trigger...",
      "Syncing contact profile to HubSpot CRM...",
      "Triggering API hub REST webhooks...",
      "Routing ticket #8942 to Live agent desk...",
      "All automated branches completed!"
    ];

    const timer = setInterval(() => {
      setWorkflowState((prev) => {
        const next = (prev + 1) % 5;
        setWorkflowLog(logs[next]);
        return next;
      });
    }, 2200);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileUA || window.innerWidth < 992);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [responseTime, setResponseTime] = useState("1.4s");
  const [resolutionRate, setResolutionRate] = useState("78.2%");
  const [resolutionChange, setResolutionChange] = useState("+8.1%");

  useEffect(() => {
    const times = ["1.4s", "1.2s", "1.5s", "1.1s", "1.3s"];
    const rates = ["78.2%", "79.5%", "78.8%", "80.4%", "79.1%"];
    const changes = ["+8.1%", "+9.4%", "+8.7%", "+10.2%", "+9.0%"];
    let idx = 0;

    const interval = setInterval(() => {
      idx = (idx + 1) % times.length;
      setResponseTime(times[idx]);
      setResolutionRate(rates[idx]);
      setResolutionChange(changes[idx]);
    }, 2200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-25% 0px -45% 0px",
      threshold: 0.1
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = cardRefs.findIndex(ref => ref.current === entry.target);
          if (index !== -1) {
            setActiveTab(index);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentRefs = cardRefs.map(ref => ref.current);
    cardRefs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      currentRefs.forEach((el) => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  const scrollToCard = (index) => {
    if (cardRefs[index]?.current) {
      const cardEl = cardRefs[index].current;
      const rectTop = cardEl.getBoundingClientRect().top;
      const absoluteTop = rectTop + window.scrollY;

      window.scrollTo({
        top: absoluteTop - 150,
        behavior: "smooth"
      });
      setActiveTab(index);
    }
  };

  return (
    <section className="chatbot-dashboard-section" style={{ height: "auto" }}>
      <div className="gradient-glow-bg"></div>

      <div className="container-fluid dashboard-inner">
        <div className="row">
          <div className="col-12">
            <div className="section-label">
              <span className="label-badge">
                <span className="dot-square active-green"></span> Solutions
              </span>
            </div>
            <h2 className="dashboard-main-title">
              Everything Your AI Business Runs On
            </h2>
          </div>
        </div>
        <div className="row g-5 mt-20">

          {/* Left Column */}
          {!isMobile && (
            <div className="col-lg-4 col-md-5 left-sticky-col">
              <div className="sticky-content-wrap">
                <ul className="dashboard-nav-menu">
                  <li
                    className={activeTab === 0 ? "active" : ""}
                    onClick={() => scrollToCard(0)}
                  >
                    <div className="nav-item-content">
                      <span className={`square-bullet ${activeTab === 0 ? "active-green" : ""}`}>■</span>
                      AI CHATBOT
                    </div>
                    <div className={`nav-underline ${activeTab === 0 ? "active" : ""}`}></div>
                  </li>
                  <li
                    className={activeTab === 1 ? "active" : ""}
                    onClick={() => scrollToCard(1)}
                  >
                    <div className="nav-item-content">
                      <span className={`square-bullet ${activeTab === 1 ? "active-green" : ""}`}>■</span>
                      WORKFLOW AUTOMATION
                    </div>
                    <div className={`nav-underline ${activeTab === 1 ? "active" : ""}`}></div>
                  </li>
                  <li
                    className={activeTab === 2 ? "active" : ""}
                    onClick={() => scrollToCard(2)}
                  >
                    <div className="nav-item-content">
                      <span className={`square-bullet ${activeTab === 2 ? "active-green" : ""}`}>■</span>
                      ANALYTICS & INSIGHT
                    </div>
                    <div className={`nav-underline ${activeTab === 2 ? "active" : ""}`}></div>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Right Column */}
          <div className={`${!isMobile ? 'col-lg-8' : 'col-12'} right-cards-col ${!isMobile ? 'mt-lg-0' : 'mt-4'}`}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "40px",
                width: "100%"
              }}
            >

              {/* Card 1: AI Chatbot */}
              <div
                ref={cardRefs[0]}
                className={`dashboard-card-wrap ${activeTab === 0 ? "active-glow" : ""}`}
              >
                <div className="corner-dot top-left"></div>
                <div className="corner-dot top-right"></div>
                <div className="corner-dot bottom-left"></div>
                <div className="corner-dot bottom-right"></div>
                <div className="divider-intersection top"></div>
                <div className="divider-intersection bottom"></div>

                <div className="row g-0 align-items-stretch">
                  <div className="col-md-6 col-12 card-text-pane">
                    <h3 className="card-pane-title">
                      AI Chatbot Solutions
                    </h3>
                    <p className="card-pane-desc">
                      Deploy hyper-intelligent, context-aware conversational AI agents that automate customer interactions, qualify leads, and provide instant assistance 24/7 across websites, portals, and chat apps.
                    </p>

                    <div className="card-checklist">
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Context-Aware RAG Retrieval Engine</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Smart Intent Classification & Sentiment</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Omnichannel (WhatsApp, Web, Slack, APIs)</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Seamless Live Agent Hybrid Routing</span>
                      </div>
                    </div>

                    <div className="card-tags-row">
                      {USE_CASES.map((useCase, index) => (
                        <span
                          key={useCase.id}
                          className={`tag-pill ${selectedCaseIndex === index ? "active" : ""}`}
                          onClick={() => handleTagClick(index)}
                          style={{ cursor: "pointer" }}
                        >
                          {useCase.label}
                        </span>
                      ))}
                    </div>

                    <div className="cta-wrapper">
                      <Link href="/services/ai-chat-bot" className="premium-card-cta">
                        Deploy AI Agent <span className="cta-arrow">↗</span>
                      </Link>
                    </div>
                  </div>

                  <div className="col-md-6 col-12 card-visual-pane">
                    <div className="visual-mockup chatbot-interface">
                      <div className="mockup-header" style={{ position: "relative", zIndex: 2 }}>
                        <div className="header-dot green"></div>
                        <span className="mockup-header-title">LIVE CHATBOT SIMULATION</span>
                      </div>

                      <div className="mockup-grid-lines"></div>

                      <div
                        className="chatbot-chat-box"
                        style={{ position: "relative", zIndex: 2 }}
                      >
                        <div className="live-chat-viewport">
                          {chatStep >= 1 && (
                            <div className="chat-bubble user">
                              <span className="bubble-text">{USE_CASES[selectedCaseIndex].userMessage}</span>
                              <span className="bubble-sender">You</span>
                            </div>
                          )}

                          {(chatStep === 1 || chatStep === 2) && (
                            <div className="chat-bubble bot-thinking">
                              <div className="thinking-dots">
                                <span></span><span></span><span></span>
                              </div>
                              <span className="bubble-sender">AI Agent is thinking...</span>
                            </div>
                          )}

                          {chatStep >= 3 && (
                            <div className="chat-bubble bot">
                              <span className="bubble-text">
                                {chatTypedBot}
                                {chatStep === 3 && chatTypedBot.length < USE_CASES[selectedCaseIndex].botMessage.length && (
                                  <span className="typing-cursor">|</span>
                                )}
                              </span>
                              <span className="bubble-sender">Assistant</span>
                            </div>
                          )}
                        </div>

                        <div className="chatbot-input-field">
                          <span className="input-prefix">&gt;</span>
                          <span className="input-text">
                            {chatStep === 0 ? chatTypedUser : (chatStep < 4 ? USE_CASES[selectedCaseIndex].userMessage : "")}
                            {chatStep === 0 && <span className="typing-cursor">|</span>}
                          </span>
                          <div className="input-action-btn">
                            <span className="arrow-btn-icon">▲</span>
                          </div>
                        </div>

                        <div className="chat-features-list">
                          <div className={`feature-item ${chatStep >= 1 ? "active-green" : ""}`}>
                            <span className="feature-dot-icon">{chatStep >= 2 ? "✓" : "❖"}</span>
                            <span className="feature-text">
                              {USE_CASES[selectedCaseIndex].steps[0]}
                            </span>
                          </div>
                          <div className={`feature-item ${chatStep >= 2 ? "active-green" : ""}`}>
                            <span className="feature-dot-icon">{chatStep >= 3 ? "✓" : "❖"}</span>
                            <span className="feature-text">
                              {USE_CASES[selectedCaseIndex].steps[1]}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`neural-retrieval-overlay ${chatStep >= 1 ? "is-active" : "is-idle"} ${chatStep === 2 ? "is-matching" : ""}`}
                        style={{ zIndex: 10 }}
                      >
                        <div className="overlay-hdr">
                          <span className={`glowing-spinner ${(chatStep === 1 || chatStep === 2) ? "is-spinning" : ""}`}>
                            ✦
                          </span>
                          <span>RAG RETRIEVAL ENGINE</span>
                        </div>
                        <div className="overlay-content">
                          <div className="status-label">LLM STATUS:</div>
                          <div className="status-val">
                            {USE_CASES[selectedCaseIndex].status[chatStep]}
                          </div>
                          <div className="db-match-progress">
                            <div
                              className="progress-bar"
                              style={{
                                width: chatStep === 2 ? "98.4%" : (chatStep >= 3 ? "100%" : "0%"),
                                transition: "width 0.8s ease"
                              }}
                            />
                          </div>

                          <div className="vector-grid-visual">
                            {Array.from({ length: 9 }).map((_, i) => {
                              const isActive = chatStep === 2 && (i === 1 || i === 4 || i === 7 || i === 8);
                              return (
                                <div
                                  key={i}
                                  className={`vector-dot ${isActive ? "pulse" : ""}`}
                                  style={{ animationDelay: `${i * 0.05}s` }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Workflow Automation */}
              <div
                ref={cardRefs[1]}
                className={`dashboard-card-wrap ${activeTab === 1 ? "active-glow" : ""}`}
              >
                <div className="corner-dot top-left"></div>
                <div className="corner-dot top-right"></div>
                <div className="corner-dot bottom-left"></div>
                <div className="corner-dot bottom-right"></div>
                <div className="divider-intersection top"></div>
                <div className="divider-intersection bottom"></div>

                <div className="row g-0 align-items-stretch">
                  <div className="col-md-6 col-12 card-text-pane">
                    <h3 className="card-pane-title">
                      Workflow Automation
                    </h3>
                    <p className="card-pane-desc">
                      Orchestrate high-volume operations and connect your stack with robust, self-healing automation loops. Eliminate manual overhead, coordinate data cross-flows, and link core platforms.
                    </p>

                    <div className="card-checklist">
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Multi-Step Conditional Branching Logic</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Bi-Directional CRM & HubSpot Data Sync</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Real-Time Webhooks & Listening Loops</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Sentiment-Based Ticket Auto-Escalation</span>
                      </div>
                    </div>

                    <div className="card-tags-row">
                      <span className="tag-pill">Multi-step Flows</span>
                      <span className="tag-pill">CRM Integration</span>
                      <span className="tag-pill">Self-Healing</span>
                    </div>

                    <div className="cta-wrapper">
                      <Link href="/services/crm-solutions" className="premium-card-cta">
                        Orchestrate Flows <span className="cta-arrow">↗</span>
                      </Link>
                    </div>
                  </div>

                  <div className="col-md-6 col-12 card-visual-pane">
                    <div className="visual-mockup workflow-interface">

                      <div className="workflow-diagram">
                        <div className={`top-cross-node ${workflowState === 0 ? "active-glow" : ""}`}>
                          <span className="cross-badge">✕</span>
                        </div>

                        <div className={`node-item parent-node ${workflowState === 0 ? "is-active" : ""}`}>
                          <span className="node-title">Chatbot Integration</span>

                          <div className="connection-line-vertical">
                            {workflowState === 0 && (
                              <div className="line-packet" />
                            )}
                          </div>
                        </div>

                        <div className={`node-item processing-node ${workflowState === 1 ? "is-active" : ""}`}>
                          <span className="node-status text-uppercase">
                            {workflowState === 4 ? "COMPLETED" : "PROCESSING"}
                          </span>
                          <span className="run-time-text">Active Flow</span>
                        </div>

                        <div className="connection-line-vertical long">
                          {workflowState === 1 && (
                            <div className="line-packet long" />
                          )}
                        </div>

                        <div className="sub-nodes-container">
                          <div className={`sub-node ${workflowState === 2 ? "is-active-crm" : ""}`}>
                            <div className="sub-node-hdr">
                              <span className={`dot green ${workflowState === 2 ? "pulse" : ""}`}>■</span>
                              <span className="sub-node-title">CRM SYNC</span>
                            </div>
                            <span className="sub-node-status">{workflowState === 2 ? "Syncing..." : "Standby"}</span>
                          </div>

                          <div className={`sub-node ${workflowState === 3 ? "is-active-api" : ""}`}>
                            <div className="sub-node-hdr">
                              <span className={`dot pink ${workflowState === 3 ? "pulse" : ""}`}>■</span>
                              <span className="sub-node-title">API HUB</span>
                            </div>
                            <span className="sub-node-status">{workflowState === 3 ? "Triggering..." : "Standby"}</span>
                          </div>

                          <div className={`sub-node ${workflowState === 4 ? "is-active-desk" : ""}`}>
                            <div className="sub-node-hdr">
                              <span className={`dot blue ${workflowState === 4 ? "pulse" : ""}`}>■</span>
                              <span className="sub-node-title">LIVE DESK</span>
                            </div>
                            <span className="sub-node-status">{workflowState === 4 ? "Connecting..." : "Standby"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="payload-ledger-console">
                        <div className="console-title">AUTOMATION TRACE</div>
                        <div className="console-logs">
                          <div key={workflowState} className="console-line ai-chat-log-enter">
                            <span className="cli-symbol">&gt;_</span> {workflowLog}
                          </div>
                          <div className="console-details">
                            <span>PAYLOAD: {"{ hook: #4819 }"}</span>
                            <span>TIME: 0.4ms</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Analytics & Insight */}
              <div
                ref={cardRefs[2]}
                className={`dashboard-card-wrap ${activeTab === 2 ? "active-glow" : ""}`}
              >
                <div className="corner-dot top-left"></div>
                <div className="corner-dot top-right"></div>
                <div className="corner-dot bottom-left"></div>
                <div className="corner-dot bottom-right"></div>
                <div className="divider-intersection top"></div>
                <div className="divider-intersection bottom"></div>

                <div className="row g-0 align-items-stretch">
                  <div className="col-md-6 col-12 card-text-pane">
                    <h3 className="card-pane-title">
                      Analytics & Insight
                    </h3>
                    <p className="card-pane-desc">
                      Deflect issues, track performance, and unlock clarity on every customer query. Direct sentiment radar and response counters keep optimization transparency at the center.
                    </p>

                    <div className="metric-stats-row">
                      <div className="single-stat-block">
                        <span className="big-number">94%</span>
                        <span className="stat-label">Faster Ticket Resolve</span>
                      </div>
                      <div className="single-stat-block">
                        <span className="big-number">82%</span>
                        <span className="stat-label">AI Deflection</span>
                      </div>
                    </div>

                    <div className="card-checklist">
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Real-Time Chat Streams & Events Logs</span>
                      </div>
                      <div className="checklist-item">
                        <span className="checklist-icon">✦</span>
                        <span className="checklist-text">Automated Topic & Intent Gap Clustering</span>
                      </div>
                    </div>

                    <div className="cta-wrapper">
                      <Link href="/services/performance-optimization" className="premium-card-cta">
                        Unlock Live Dashboard <span className="cta-arrow">↗</span>
                      </Link>
                    </div>
                  </div>

                  <div className="col-md-6 col-12 card-visual-pane">
                    <div className="visual-mockup analytics-interface">
                      <div className="analytics-metrics-grid">
                        <div className="metric-box">
                          <span className="metric-box-title">AVG RESPONSE TIME</span>
                          <div className="metric-box-value">
                            <span className="value-number">{responseTime}</span>
                            <span className="change-badge green">↗ +12.8%</span>
                          </div>
                        </div>

                        <div className="metric-box">
                          <span className="metric-box-title">AI RESOLUTION RATE</span>
                          <div className="metric-box-value">
                            <span className="value-number">{resolutionRate}</span>
                            <span className="change-badge green">↗ {resolutionChange}</span>
                          </div>
                        </div>
                      </div>

                      <div className="chart-container-simulated">
                        <div className="chart-bars-wrap">
                          {[12, 20, 10, 25, 18, 35, 30, 48, 40, 56, 48, 65, 58, 75, 68, 85, 78, 100].map((val, idx) => (
                            <div
                              key={idx}
                              className="chart-bar"
                              style={{ "--bar-height": `${val}%`, "--bar-index": idx }}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="sentiment-pulse-radar">
                        <div className="radar-circle-wrap">
                          <div className="radar-beam" />
                          <div className="radar-ping" />
                          <div className="radar-core">AI</div>
                        </div>
                        <div className="radar-metrics">
                          <span className="radar-title">SENTIMENT RADAR</span>
                          <span className="radar-val">98.4% POSITIVE</span>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
